import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiChevronDown, mdiChevronRight, mdiDownload, mdiInformationOutline } from '@mdi/js'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { useLocale } from '../../contexts/LocaleContext'
import type { GroupCell, GroupKPIMeta, GroupPeers, GroupReading, KPIText, Tier } from '../../api/groupSteering'
import { SURVEY_TONES } from '../conversations/thread'
import { cn } from '../../lib/utils'
import { TIER_BG, TIER_TEXT, formatDelta, formatUnit, gaugeRatio, langOf, npsUncertainty, text, tierOf, type NpsCounts } from './format'

/**
 * The small building blocks of the group console — a tier badge, a tile that
 * carries its own definition, a section with its Method and Reading, the
 * four-figure summary. Every colour is a token, every label comes from the
 * registry through the props.
 */

/** A tier with its bound: "Sain · 12 h – 24 h". Pattern §29. */
export function TierBadge({ tier, bound, testId }: { tier: Tier; bound?: string; testId?: string }) {
    const { t } = useTranslation('group-steering')
    return (
        <Badge variant="outline" className={`${TIER_BG[tier]} ${TIER_TEXT[tier]} font-medium whitespace-nowrap`} data-testid={testId ?? 'tier-badge'}>
            {t(`tier.${tier}`)}
            {bound ? <span className="ml-1 font-normal text-muted-foreground">· {bound}</span> : null}
        </Badge>
    )
}

/** The bound line of a tier, from the effective tiers: "12 h – 24 h". */
export function tierBound(kpi: GroupKPIMeta, tier: Tier, lang: 'en' | 'fr', locale: string): string {
    const ex = kpi.target.tier_excellent
    const wa = kpi.target.tier_watch
    if (ex === undefined || wa === undefined) return ''
    const f = (v: number) => formatUnit(v, kpi.unit, lang, locale)
    const better = kpi.direction === 'higher_better'
    switch (tier) {
        case 'excellent':
            return better ? `≥ ${f(ex)}` : `≤ ${f(ex)}`
        case 'healthy':
            return better ? `${f(wa)} – ${f(ex)}` : `${f(ex)} – ${f(wa)}`
        case 'watch':
            return better ? `< ${f(wa)}` : `> ${f(wa)}`
        default:
            return ''
    }
}

/** The "i" that opens the full definition of an indicator. */
export function DefinitionPopover({ kpi }: { kpi: GroupKPIMeta }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                    aria-label={t('consolidated.definition')}
                    title={t('consolidated.definition')}
                    data-testid={`kpi-definition-${kpi.code}`}
                >
                    <AppIcon path={mdiInformationOutline} size="xs" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-sm space-y-2" align="end">
                <div className="font-medium text-foreground">{text(kpi.label, lang)}</div>
                <p className="text-muted-foreground">{text(kpi.definition, lang)}</p>
                <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{t('definitions.denominator')}</span> · {text(kpi.denominator, lang)}
                </p>
                {kpi.confusable && (
                    <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{t('definitions.confusable')}</span> · {text(kpi.confusable, lang)}
                    </p>
                )}
                <p className="text-[11px] text-muted-foreground font-mono">
                    {kpi.code} · v{kpi.version}
                </p>
            </PopoverContent>
        </Popover>
    )
}

/**
 * A tile: icon-less by design (the family card carries the icon), label,
 * definition as subtitle, pooled value, gauge for a ratio, delta against the
 * previous disjoint window, and a foot with the target and the out-of-target
 * count — or n and the uncertainty for the NPS. Never a perimeter's name.
 */
export function KpiTile({
    kpi,
    cell,
    outOfTarget,
    peers,
    selected,
    onSelect,
}: {
    kpi: GroupKPIMeta
    cell: GroupCell
    outOfTarget: { count: number; total: number }
    peers: GroupPeers
    selected?: boolean
    onSelect?: () => void
}) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const tier = tierOf(cell)
    const gauge = gaugeRatio(cell.value, kpi.unit)
    const uncertainty = kpi.code === 'nps' ? npsUncertainty(cell.n) : null
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={selected}
            className={`text-left rounded-lg border p-3 transition-colors ${selected ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'hover:bg-muted/40'} ${kpi.pilot ? '' : 'opacity-80'}`}
            data-testid={`kpi-tile-${kpi.code}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                        {kpi.pilot && <span className="mr-1 text-[var(--chart-4)]">★</span>}
                        {text(kpi.label, lang)}
                    </div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2">{text(kpi.denominator, lang)}</div>
                </div>
                <DefinitionPopover kpi={kpi} />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
                <span className={`text-2xl font-semibold tabular-nums ${TIER_TEXT[tier]}`} data-testid={`kpi-tile-${kpi.code}-value`}>
                    {cell.suppressed ? '—' : formatUnit(cell.value, kpi.unit, lang, locale)}
                </span>
                {cell.delta_vs_previous !== undefined && !cell.suppressed && (
                    <span className="text-xs text-muted-foreground tabular-nums" title={t('consolidated.delta_previous')}>
                        {formatDelta(cell.delta_vs_previous, kpi.unit, lang, locale)}
                    </span>
                )}
            </div>
            {gauge !== null && !cell.suppressed && (
                <div className="mt-1.5 h-1.5 w-full rounded bg-muted overflow-hidden" aria-hidden>
                    <div className="h-full rounded" style={{ width: `${gauge * 100}%`, background: `var(${tier === 'watch' ? '--destructive' : tier === 'excellent' ? '--chart-2' : '--chart-1'})` }} />
                </div>
            )}
            <div className="mt-2 text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                {cell.suppressed ? (
                    <span>{t('consolidated.suppressed')}</span>
                ) : (
                    <span>{t('consolidated.n', { count: cell.n })}</span>
                )}
                {uncertainty !== null && !cell.suppressed && <span>{t('consolidated.uncertainty', { value: uncertainty })}</span>}
                {kpi.target.target !== undefined && <span>{t('consolidated.target', { value: formatUnit(kpi.target.target, kpi.unit, lang, locale) })}</span>}
                {outOfTarget.total > 0 && (
                    <span className={outOfTarget.count > 0 ? 'text-destructive' : ''}>
                        {outOfTarget.count > 0
                            ? t('consolidated.out_of_target', { count: outOfTarget.count, total: outOfTarget.total })
                            : t('consolidated.none_out_of_target')}
                    </span>
                )}
                {cell.coverage !== undefined && cell.coverage < 0.8 && (
                    <span className="text-[var(--chart-4)]" title={t('consolidated.coverage', { value: `${Math.round(cell.coverage * 100)} %` })}>
                        ⚠ {t('consolidated.coverage', { value: `${Math.round(cell.coverage * 100)} %` })}
                    </span>
                )}
                {peers.shown && peers.p50 !== undefined && (
                    <span>{t('consolidated.summary_peers')} {formatUnit(peers.p50, kpi.unit, lang, locale)}</span>
                )}
            </div>
        </button>
    )
}

/** The four figures at the top of a section. */
export function FourFigures({
    kpi,
    pooled,
    peers,
    outOfTarget,
    trend,
}: {
    kpi: GroupKPIMeta
    pooled: GroupCell | undefined
    peers: GroupPeers | undefined
    outOfTarget: { count: number; total: number }
    trend?: number
}) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const figure = (label: string, value: string, sub?: string, testId?: string) => (
        <div className="min-w-0" data-testid={testId}>
            <div className="text-lg font-semibold tabular-nums text-foreground">{value}</div>
            <div className="text-[11px] text-muted-foreground">{label}</div>
            {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
        </div>
    )
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`section-figures-${kpi.code}`}>
            {figure(t('consolidated.summary_pooled'), pooled && !pooled.suppressed ? formatUnit(pooled.value, kpi.unit, lang, locale) : '—', pooled ? t('consolidated.n', { count: pooled.n }) : undefined)}
            {figure(
                t('consolidated.summary_peers'),
                peers?.shown && peers.p50 !== undefined ? formatUnit(peers.p50, kpi.unit, lang, locale) : '—',
                peers?.shown && peers.p10 !== undefined && peers.p90 !== undefined
                    ? `${formatUnit(peers.p10, kpi.unit, lang, locale)} – ${formatUnit(peers.p90, kpi.unit, lang, locale)} · ${t('consolidated.summary_band', { count: peers.count })}`
                    : peers && peers.count > 0
                      ? t('consolidated.peers_band_hidden', { count: 5 })
                      : undefined,
            )}
            {figure(t('consolidated.summary_out'), outOfTarget.total > 0 ? `${outOfTarget.count} / ${outOfTarget.total}` : '—')}
            {figure(t('consolidated.summary_trend'), trend === undefined ? '—' : formatDelta(trend, kpi.unit, lang, locale), t('consolidated.delta_previous'))}
        </div>
    )
}

/** Method and Reading, mandatory under every chart. */
export function ReadingBlock({ reading }: { reading: GroupReading }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const paragraphs = reading.paragraphs[lang] ?? reading.paragraphs.en ?? []
    return (
        <div className="grid md:grid-cols-2 gap-4 text-sm" data-testid="reading-block">
            <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{t('method.title')}</div>
                <p className="text-muted-foreground">{text(reading.method, lang)}</p>
            </div>
            <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{t('method.reading_title')}</div>
                {paragraphs.map((p, i) => (
                    <p key={i} className={i === paragraphs.length - 1 ? 'text-[11px] text-muted-foreground italic mt-1' : 'text-foreground'}>
                        {p}
                    </p>
                ))}
                {reading.generated_from.length > 0 && (
                    <details className="mt-1 text-[11px] text-muted-foreground">
                        <summary className="cursor-pointer">{t('method.generated_from')}</summary>
                        <span className="font-mono">{reading.generated_from.map((g) => `${g.code}=${typeof g.value === 'number' ? Math.round(g.value * 100) / 100 : String(g.value)}`).join(' · ')}</span>
                    </details>
                )}
                {reading.reworded && <div className="text-[11px] text-muted-foreground">{t('method.reworded')}</div>}
            </div>
        </div>
    )
}

/**
 * A collapsible section with the same banner everywhere: title, key-value
 * badge, one-sentence description, chart/table toggle, CSV export. The body
 * is the same sequence on every page: four figures → picker → one-axis
 * chart → last-month line → Method → Reading. Pattern §28.
 */
export function Section({
    id,
    title,
    badge,
    description,
    defaultOpen = true,
    view,
    onViewChange,
    csv,
    children,
}: {
    id: string
    title: string
    badge?: ReactNode
    description: string
    defaultOpen?: boolean
    view?: 'chart' | 'table'
    onViewChange?: (v: 'chart' | 'table') => void
    csv?: () => string
    children: ReactNode
}) {
    const { t } = useTranslation('group-steering')
    const [open, setOpen] = useState<boolean>(() => {
        try {
            const stored = localStorage.getItem(`group-section:${id}`)
            return stored === null ? defaultOpen : stored === '1'
        } catch {
            return defaultOpen
        }
    })
    const toggle = () => {
        setOpen((o) => {
            try {
                localStorage.setItem(`group-section:${id}`, o ? '0' : '1')
            } catch {
                /* per-viewer convenience only */
            }
            return !o
        })
    }
    const download = () => {
        if (!csv) return
        const blob = new Blob([csv()], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${id}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }
    return (
        <section className="rounded-lg border bg-card" data-testid={`section-${id}`}>
            <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                <button type="button" onClick={toggle} aria-expanded={open} className="flex items-center gap-2 text-left min-w-0 flex-1" data-testid={`section-${id}-toggle`}>
                    <AppIcon tone="muted" path={open ? mdiChevronDown : mdiChevronRight} size="sm" className="flex-shrink-0" />
                    <span className="font-medium text-foreground truncate">{title}</span>
                    {badge}
                </button>
                {onViewChange && (
                    <div className="inline-flex rounded-md border p-0.5" role="tablist">
                        {(['chart', 'table'] as const).map((v) => (
                            <button
                                key={v}
                                type="button"
                                role="tab"
                                aria-selected={view === v}
                                onClick={() => onViewChange(v)}
                                className={`px-2 py-0.5 text-xs rounded ${view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                data-testid={`section-${id}-view-${v}`}
                            >
                                {t(`consolidated.${v}`)}
                            </button>
                        ))}
                    </div>
                )}
                {csv && (
                    <Button variant="outline" size="sm" onClick={download} aria-label={t('consolidated.export')} title={t('consolidated.export')} data-testid={`section-${id}-export`}>
                        <AppIcon path={mdiDownload} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('consolidated.export')}</span>
                    </Button>
                )}
            </div>
            <p className="px-4 -mt-1 pb-2 text-xs text-muted-foreground">{description}</p>
            {open && <div className="px-4 pb-4 space-y-4">{children}</div>}
        </section>
    )
}

/** Pills to pick the indicator of a section. */
export function KpiPills({ kpis, value, onChange, lang }: { kpis: GroupKPIMeta[]; value: string; onChange: (code: string) => void; lang: 'en' | 'fr' }) {
    return (
        <div className="flex flex-wrap gap-1.5" role="radiogroup">
            {kpis.map((k) => (
                <button
                    key={k.code}
                    type="button"
                    role="radio"
                    aria-checked={value === k.code}
                    onClick={() => onChange(k.code)}
                    className={`px-2 py-0.5 rounded-full border text-xs ${value === k.code ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`kpi-pill-${k.code}`}
                >
                    {k.pilot ? '★ ' : ''}
                    {text(k.label, lang)}
                </button>
            ))}
        </div>
    )
}

/**
 * The NPS composition on one line: detractors, passives, promoters, each
 * segment as wide as its share, low scores on the left like the 0-10 scale, in the closing survey's class colours
 * (`SURVEY_TONES`). No answer draws the empty track, never a guess. The
 * counts are the reading; the bar only shapes them.
 */
export function NpsCompositionBar({ counts, className, testId }: { counts: NpsCounts; className?: string; testId?: string }) {
    const { t } = useTranslation('group-steering')
    const label = t('sites.composition_label', { promoters: counts.promoters, passives: counts.passives, detractors: counts.detractors })
    const segments = [
        { key: 'detractors', count: counts.detractors, tone: SURVEY_TONES.detractor },
        { key: 'passives', count: counts.passives, tone: SURVEY_TONES.passive },
        { key: 'promoters', count: counts.promoters, tone: SURVEY_TONES.promoter },
    ]
    return (
        <div
            role="img"
            aria-label={label}
            title={label}
            className={cn('flex h-2 w-full overflow-hidden rounded-full bg-muted', className)}
            data-testid={testId ?? 'nps-composition-bar'}
            data-total={counts.total}
        >
            {counts.total > 0 &&
                segments
                    .filter((s) => s.count > 0)
                    .map((s) => <div key={s.key} className={`h-full ${s.tone}`} style={{ width: `${(s.count / counts.total) * 100}%` }} data-segment={s.key} />)}
        </div>
    )
}

/** A bilingual label, once. */
export function T({ t, lang }: { t: KPIText | undefined; lang: 'en' | 'fr' }) {
    return <>{text(t, lang)}</>
}
